import type { EnvironmentId, EnvironmentApi } from "@cmmd-forge/contracts";
import { useMemo, useSyncExternalStore } from "react";

import type { WsRpcClient } from "./rpc/wsRpcClient";
import {
  readEnvironmentConnection,
  subscribeEnvironmentConnections,
} from "./environments/runtime/service";

export function createEnvironmentApi(rpcClient: WsRpcClient): EnvironmentApi {
  const browserClient = rpcClient.browser;
  const browserApi: EnvironmentApi["browser"] | undefined = browserClient
    ? {
        openSession: browserClient.openSession,
        listSessions: browserClient.listSessions,
        closeSession: browserClient.closeSession,
        getSession: browserClient.getSession,
        command: browserClient.command,
        clearBrowsingData: browserClient.clearBrowsingData,
        attachDisplay: browserClient.attachDisplay,
        addDisplayIceCandidate: async (input) => {
          await browserClient.addDisplayIceCandidate(input);
        },
        onEvent: (input, callback) => browserClient.onEvent(input, callback),
      }
    : undefined;
  return {
    ...(browserApi ? { browser: browserApi } : {}),
    attachments: {
      createUploadUrl: (input) => rpcClient.attachments.createUploadUrl(input),
      delete: (input) => rpcClient.attachments.delete(input),
    },
    computer: {
      input: (input) => rpcClient.computer.input(input),
    },
    threadDevRuntime: {
      get: (input) => rpcClient.threadDevRuntime.get(input),
      allocatePort: (input) => rpcClient.threadDevRuntime.allocatePort(input),
      release: (input) => rpcClient.threadDevRuntime.release(input),
    },
    devTunnel: {
      register: (input) => rpcClient.devTunnel.register(input),
      unregister: (input) => rpcClient.devTunnel.unregister(input),
      list: (input) => rpcClient.devTunnel.list(input),
    },
    terminal: {
      open: (input) => rpcClient.terminal.open(input as never),
      write: (input) => rpcClient.terminal.write(input as never),
      resize: (input) => rpcClient.terminal.resize(input as never),
      signal: (input) => rpcClient.terminal.signal(input as never),
      clear: (input) => rpcClient.terminal.clear(input as never),
      restart: (input) => rpcClient.terminal.restart(input as never),
      close: (input) => rpcClient.terminal.close(input as never),
      onEvent: (callback) => rpcClient.terminal.onEvent(callback),
    },
    projects: {
      searchContents: rpcClient.projects.searchContents,
      searchEntries: rpcClient.projects.searchEntries,
      readFile: rpcClient.projects.readFile,
      writeFile: rpcClient.projects.writeFile,
    },
    workspace: {
      environmentPreflight: rpcClient.workspace.environmentPreflight,
      listDirectory: rpcClient.workspace.listDirectory,
      ensureScratch: rpcClient.workspace.ensureScratch,
    },
    shell: {
      openInEditor: (input) => rpcClient.shell.openInEditor(input),
    },
    git: {
      pull: rpcClient.git.pull,
      refreshStatus: rpcClient.git.refreshStatus,
      onStatus: (input, callback, options) => rpcClient.git.onStatus(input, callback, options),
      listBranches: rpcClient.git.listBranches,
      createWorktree: rpcClient.git.createWorktree,
      removeWorktree: rpcClient.git.removeWorktree,
      createBranch: rpcClient.git.createBranch,
      checkout: rpcClient.git.checkout,
      init: rpcClient.git.init,
      resolvePullRequest: rpcClient.git.resolvePullRequest,
      preparePullRequestThread: rpcClient.git.preparePullRequestThread,
    },
    pullRequests: {
      list: (input) => rpcClient.pullRequests.list(input),
      listStats: (input) => rpcClient.pullRequests.listStats(input),
      detail: (input) => rpcClient.pullRequests.detail(input),
      activity: (input) => rpcClient.pullRequests.activity(input),
      threadComments: (input) => rpcClient.pullRequests.threadComments(input),
      diffFileContents: (input) => rpcClient.pullRequests.diffFileContents(input),
      runAction: (input) => rpcClient.pullRequests.runAction(input),
      update: (input) => rpcClient.pullRequests.update(input),
      comment: (input) => rpcClient.pullRequests.comment(input),
      updateComment: (input) => rpcClient.pullRequests.updateComment(input),
      submitReview: (input) => rpcClient.pullRequests.submitReview(input),
      replyToThread: (input) => rpcClient.pullRequests.replyToThread(input),
      setThreadResolution: (input) => rpcClient.pullRequests.setThreadResolution(input),
      setReaction: (input) => rpcClient.pullRequests.setReaction(input),
      invalidate: (input) => rpcClient.pullRequests.invalidate(input),
      reviewerCandidates: (input) => rpcClient.pullRequests.reviewerCandidates(input),
      requestReviewers: (input) => rpcClient.pullRequests.requestReviewers(input),
    },
    orchestration: {
      dispatchCommand: rpcClient.orchestration.dispatchCommand,
      searchThreads: rpcClient.orchestration.searchThreads,
      getTurnDiff: rpcClient.orchestration.getTurnDiff,
      getFullThreadDiff: rpcClient.orchestration.getFullThreadDiff,
      subscribeShell: (callback, options) =>
        rpcClient.orchestration.subscribeShell(callback, options),
      subscribeThread: (input, callback, options) =>
        rpcClient.orchestration.subscribeThread(input, callback, options),
    },
  };
}

export function readEnvironmentApi(environmentId: EnvironmentId): EnvironmentApi | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  if (!environmentId) {
    return undefined;
  }

  const connection = readEnvironmentConnection(environmentId);
  return connection ? createEnvironmentApi(connection.client) : undefined;
}

export function useEnvironmentApi(environmentId: EnvironmentId): EnvironmentApi | undefined {
  const connection = useSyncExternalStore(
    subscribeEnvironmentConnections,
    () => readEnvironmentConnection(environmentId),
    () => null,
  );
  return useMemo(
    () => (connection ? createEnvironmentApi(connection.client) : undefined),
    [connection],
  );
}

export function ensureEnvironmentApi(environmentId: EnvironmentId): EnvironmentApi {
  const api = readEnvironmentApi(environmentId);
  if (!api) {
    throw new Error(`Environment API not found for environment ${environmentId}`);
  }
  return api;
}
